<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ProjectRow from './ProjectRow.vue';
import CreateProjectModal from './CreateProjectModal.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const searchQuery = ref('');
const createModalRef = ref(null);

const projects = computed(() => store.getters.getProjects);
const uiFlags = computed(() => store.getters.getProjectUIFlags);

const filteredProjects = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return projects.value;
  return projects.value.filter(p =>
    [p.name, p.empresa?.name, p.empresa_name, p.status].some(v =>
      (v || '').toLowerCase().includes(q)
    )
  );
});

const isEmpty = computed(
  () => !uiFlags.value.isFetching && !filteredProjects.value.length
);

const loadProjects = () => store.dispatch('fetchProjects');

const openProject = project =>
  router.push({ name: 'project_details', params: { projectId: project.id } });

const openPlaybooks = () => router.push({ name: 'playbooks_list' });

const onCreated = project => openProject(project);

onMounted(loadProjects);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex items-center justify-between gap-3 px-6 pt-5 pb-3 border-b shrink-0 border-n-weak"
    >
      <h1 class="flex items-center gap-2 text-xl font-semibold text-n-slate-12">
        {{ t('PROJECTS.HEADER_TITLE') }}
        <span class="text-base font-normal text-n-slate-10">
          {{ projects.length }}
        </span>
      </h1>
      <div class="flex items-center gap-2">
        <div class="relative w-52 max-w-full">
          <span
            class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10 size-4"
          />
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('PROJECTS.SEARCH_PLACEHOLDER')"
            class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>
        <Button
          :label="t('PROJECTS.PLAYBOOKS.MANAGE')"
          icon="i-lucide-book-open"
          size="sm"
          variant="faded"
          color="slate"
          @click="openPlaybooks"
        />
        <Button
          :label="t('PROJECTS.ACTIONS.NEW')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="createModalRef?.open()"
        />
      </div>
    </header>

    <!-- Body -->
    <section class="flex-1 min-h-0 overflow-y-auto">
      <!-- Loading skeleton -->
      <div
        v-if="uiFlags.isFetching && !projects.length"
        class="divide-y divide-n-weak/60"
      >
        <div
          v-for="n in 6"
          :key="n"
          class="flex items-center gap-3 px-6 py-3 animate-pulse"
        >
          <span class="w-16 h-3 rounded bg-n-alpha-2 shrink-0" />
          <div class="flex flex-col gap-1.5">
            <span class="h-3 rounded w-36 bg-n-alpha-2" />
            <span class="h-2.5 rounded w-24 bg-n-alpha-1" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="isEmpty"
        class="flex flex-col items-center justify-center h-full gap-4 px-6 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-14 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-folder-kanban size-7" />
        </span>
        <div class="flex flex-col gap-1">
          <p class="text-base font-medium text-n-slate-12">
            {{
              searchQuery ? t('PROJECTS.NO_RESULTS') : t('PROJECTS.EMPTY_TITLE')
            }}
          </p>
          <p class="max-w-sm text-sm text-n-slate-10">
            {{
              searchQuery ? t('PROJECTS.NO_RESULTS_HINT') : t('PROJECTS.EMPTY')
            }}
          </p>
        </div>
        <div v-if="!searchQuery" class="flex items-center gap-2">
          <Button
            :label="t('PROJECTS.ACTIONS.NEW')"
            icon="i-lucide-plus"
            size="sm"
            color="blue"
            @click="createModalRef?.open()"
          />
          <Button
            :label="t('PROJECTS.PLAYBOOKS.START_FROM')"
            icon="i-lucide-book-open"
            size="sm"
            variant="faded"
            color="slate"
            @click="openPlaybooks"
          />
        </div>
      </div>

      <!-- List -->
      <div v-else class="flex flex-col">
        <!-- Column header -->
        <div
          class="flex items-center gap-3 px-6 py-2 text-xs border-b border-n-weak text-n-slate-10 shrink-0"
        >
          <span class="w-16 shrink-0">{{ t('PROJECTS.COL.ID') }}</span>
          <span class="w-56 shrink-0">{{ t('PROJECTS.COL.NAME') }}</span>
          <span class="hidden sm:block w-24 shrink-0">
            {{ t('PROJECTS.COL.STATUS') }}
          </span>
          <span class="hidden md:block w-24 shrink-0">
            {{ t('PROJECTS.COL.HEALTH') }}
          </span>
          <span class="flex-1 min-w-0">{{ t('PROJECTS.COL.PROGRESS') }}</span>
          <span class="hidden lg:block w-28 shrink-0 px-2">
            {{ t('PROJECTS.COL.OWNER') }}
          </span>
          <span class="size-4 shrink-0" />
        </div>

        <div class="divide-y divide-n-weak/60">
          <ProjectRow
            v-for="project in filteredProjects"
            :key="project.id"
            :project="project"
            @select="openProject"
          />
        </div>
      </div>
    </section>

    <CreateProjectModal ref="createModalRef" @created="onCreated" />
  </div>
</template>
